import type { FC } from 'react';
import React, { useState, useCallback } from 'react';
import { match } from 'variant';
import { Spinner, Box, Text } from '@wix/cli-ui-kit';
import { useTranslation, Trans } from '@wix/cli-i18n';
import { useLogsServer } from './useLogsServer.js';
import type { AggregatedLog } from './logsReqHandler.js';
import { ServerLogMessage } from './ServerLogMessage.js';
import { useProjectModel } from '../../model.js';

export const LogsCommand: FC<{
  version: string;
}> = ({ version }) => {
  const { model } = useProjectModel();
  const { t } = useTranslation();
  const [logs, setLogs] = useState<AggregatedLog[]>([]);
  const serverResult = useLogsServer({
    appId: model.config.appId,
    version,
    onLog: useCallback(
      (serverLogs) => {
        setLogs((prevLogs) => {
          return [...prevLogs, ...serverLogs];
        });
      },
      [setLogs]
    ),
  });

  return match(serverResult, {
    Success: () => (
      <Box marginTop={1} marginBottom={1} flexDirection="column">
        <Box marginBottom={1} flexDirection="column">
          <Text>
            <Trans i18nKey="logs_command.listening_to_logs" />
          </Text>
          <Text>
            <Trans i18nKey="logs_command.prod_env_logs" values={{ version }} />
          </Text>
        </Box>

        {logs.map((log, logIdx) => (
          <Box key={logIdx} flexDirection="column">
            <Box columnGap={1}>
              <Box flexShrink={0}>
                <Text skin="secondary">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </Text>
              </Box>
              <Box columnGap={1}>
                {match(log, {
                  Log: ({ message, level }) =>
                    message.map((msgItem, idx) => (
                      <ServerLogMessage
                        key={`${logIdx}-${idx}`}
                        message={msgItem}
                        level={level}
                      />
                    )),
                  Exception: ({ message }) => (
                    <ServerLogMessage
                      key={logIdx}
                      message={message}
                      level="error"
                    />
                  ),
                })}
              </Box>
            </Box>
            {match(log, {
              Log: () => null,
              Exception: ({ stack }) => <Text skin="error">{stack}</Text>,
            })}
          </Box>
        ))}
      </Box>
    ),
    Loading: () => <Spinner text={t('logs_command.connecting')} />,
    Error: () => null,
  });
};
